/* eslint-disable no-magic-numbers */
import { RULES_ADD_SUB_DATA } from '../../games/data/vifRulesAddSubData';
import {
  type GenerateRule,
  buildProblemSet,
  flag11,
  flag12Mixed,
  flag12Strict,
  flag5Round1,
  flag5Round2,
  flag5Round3,
  flag5Round4,
} from '../../games/engine/ProblemSource';
import {
  carryForwardUnchosen,
  pickSixRandomThenCarryUnchosen,
} from '../../games/engine/carryForward';

import type { ProblemPool } from './deal';
import {
  createRandomInt,
  isFirstTime,
  loadPool,
  markVisited,
  zeros,
} from './rulesProblemState';

/**
 * Problem-pool selection for the six VIF addition rules variants (gameFlags
 * 0-5 of `VIFRulesAddSub`; `computeOperation` treats `gameFlag > 5` as
 * subtraction).
 *
 * This is a direct port of `VIFRulesAddSub.populateProblem`'s flag 0-5
 * branches with the engine's `this` state lifted into an explicit
 * `RulesAddSubProblemState`. The zero-filled `blnChosen` lengths are copied
 * verbatim rather than derived, because `carryForwardUnchosen` iterates that
 * array's length - shortening it would silently drop problems from the
 * carried-forward pool.
 */

export const RULES_ADD_SUB_FLAGS = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
] as const;

export type RulesAddSubFlag = (typeof RULES_ADD_SUB_FLAGS)[number];

export function isRulesAddSubFlag(flag: number): flag is RulesAddSubFlag {
  return (RULES_ADD_SUB_FLAGS as readonly number[]).includes(flag);
}

/** Mutable pool/memory carried across layers, mirroring the engine fields. */
export interface RulesAddSubProblemState {
  /** Legacy `topEQs`. */
  topEQs: string[];
  /** Legacy `bottomAnswers`. */
  bottomAnswers: string[];
  /** Legacy `blnChosen` - per-layer "already used" memory. */
  blnChosen: number[][];
  /** Legacy `blnFirstTimeLayer`. */
  blnFirstTimeLayer: number[];
}

export function createRulesAddSubProblemState(): RulesAddSubProblemState {
  return {
    topEQs: [],
    bottomAnswers: [],
    blnChosen: [[0], [0], [0], [0]],
    blnFirstTimeLayer: [1, 1, 1, 1],
  };
}

export interface PopulateArgs {
  flag: RulesAddSubFlag;
  /** Legacy `intLayer`, 1-based. */
  layer: number;
  /** Legacy `gameFrame.round`, 1-based. */
  round: number;
  /** Legacy `intColumns`, permanently 3. */
  columns: number;
  state: RulesAddSubProblemState;
  rng?: () => number;
  randomInt?: (min: number, max: number) => number;
}

/**
 * Advances `state` to the pool for the given flag/layer/round and returns it.
 * Mutates `state` in place, exactly as the legacy engine does.
 */
export function populateRulesAddSubProblems({
  flag,
  layer,
  round,
  columns,
  state,
  rng = Math.random,
  randomInt = createRandomInt(rng),
}: PopulateArgs): ProblemPool {
  switch (flag) {
    case 0:
      populateFlag0(layer, state);
      break;
    case 1:
      populateFlag1(layer, state, randomInt);
      break;
    case 2:
      populatePickSixCarryForward(
        layer,
        state,
        RULES_ADD_SUB_DATA.flag2.layer1,
        rng,
      );
      break;
    case 3:
      populateFourLayerTables(
        layer,
        state,
        RULES_ADD_SUB_DATA.flag3.layer1,
        RULES_ADD_SUB_DATA.flag3.layer3,
      );
      break;
    case 4:
      populateFlag4(layer, round, state);
      break;
    case 5:
      populateFlag5(layer, round, columns, state, randomInt);
      break;
    case 6:
      populateTwoLayerTables(
        layer,
        state,
        RULES_ADD_SUB_DATA.flag6.layer1,
        RULES_ADD_SUB_DATA.flag6.layer2,
      );
      break;
    case 7:
      populateTwoLayerTables(
        layer,
        state,
        RULES_ADD_SUB_DATA.flag7.layer1,
        RULES_ADD_SUB_DATA.flag7.layer2,
      );
      break;
    case 8:
      populatePickSixCarryForward(
        layer,
        state,
        RULES_ADD_SUB_DATA.flag8.layer1,
        rng,
      );
      break;
    case 9:
      populateFourLayerTables(
        layer,
        state,
        RULES_ADD_SUB_DATA.flag9.layer1,
        RULES_ADD_SUB_DATA.flag9.layer3,
      );
      break;
    case 10:
      populateFourLayerTables(
        layer,
        state,
        RULES_ADD_SUB_DATA.flag10.layer1,
        RULES_ADD_SUB_DATA.flag10.layer3,
      );
      break;
    case 11:
    case 12:
      populateGenerated(flag, layer, round, columns, state, randomInt);
      break;
  }

  return { topEQs: state.topEQs, bottomAnswers: state.bottomAnswers };
}

function populateFlag0(layer: number, state: RulesAddSubProblemState): void {
  if (layer == 1) {
    if (isFirstTime(state, layer)) {
      loadPool(state, RULES_ADD_SUB_DATA.flag0.layer1);
      markVisited(state, layer);
    }
    state.blnChosen[layer - 1] = zeros(19);
  } else {
    if (isFirstTime(state, layer)) {
      carryForwardUnchosen(
        state.topEQs,
        state.bottomAnswers,
        state.blnChosen[0]!,
      );
      markVisited(state, layer);
    }
    state.blnChosen[1] = zeros(10);
  }
}

function populateFlag1(
  layer: number,
  state: RulesAddSubProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  if (layer == 1) {
    loadPool(state, RULES_ADD_SUB_DATA.flag1.layer1);
    if (isFirstTime(state, layer)) {
      markVisited(state, layer);
    }
    state.blnChosen[layer - 1] = zeros(17);
    return;
  }

  if (isFirstTime(state, layer)) {
    markVisited(state, layer);

    const arrHolder = [...state.topEQs];
    const arrHolder2 = [...state.bottomAnswers];
    state.topEQs = [];
    state.bottomAnswers = [];

    // One problem that was used in layer 1 ...
    const chosenRow = state.blnChosen[0]!;
    let picked = randomInt(0, chosenRow.length - 1);
    while (chosenRow[picked] == 0) {
      picked = randomInt(0, chosenRow.length - 1);
    }
    state.topEQs.push(arrHolder[picked]!);
    state.bottomAnswers.push(arrHolder2[picked]!);

    // ... then the eight that were not.
    for (let j = 0; j < chosenRow.length; j++) {
      if (chosenRow[j] == 0) {
        state.topEQs.push(arrHolder[j]!);
        state.bottomAnswers.push(arrHolder2[j]!);
      }
    }

    state.blnChosen[1] = zeros(9);
  } else {
    // Re-entering layer 2 after a mistake: reshuffle from the layer 1 pool.
    loadPool(state, RULES_ADD_SUB_DATA.flag1.layer1);
    state.blnChosen[1] = zeros(17);
  }
}

/**
 * Two layers, each with its own table. Legacy flags 6 and 7.
 */
function populateTwoLayerTables(
  layer: number,
  state: RulesAddSubProblemState,
  layer1: ProblemPool,
  layer2: ProblemPool,
): void {
  if (isFirstTime(state, layer)) {
    loadPool(state, layer == 1 ? layer1 : layer2);
    markVisited(state, layer);
  }
  state.blnChosen[layer - 1] = zeros(9);
}

/**
 * Layer 1 from a table, layer 2 built from six problems layer 1 used plus
 * everything it did not. Legacy flags 2 and 8, which share a body.
 */
function populatePickSixCarryForward(
  layer: number,
  state: RulesAddSubProblemState,
  layer1: ProblemPool,
  rng: () => number,
): void {
  if (layer == 1) {
    if (isFirstTime(state, layer)) {
      loadPool(state, layer1);
      markVisited(state, layer);
    }
    state.blnChosen[layer - 1] = zeros(12);
  } else {
    if (isFirstTime(state, layer)) {
      pickSixRandomThenCarryUnchosen(
        state.topEQs,
        state.bottomAnswers,
        state.blnChosen[0]!,
        zeros(12),
        rng,
      );
      markVisited(state, layer);
    }
    state.blnChosen[1] = zeros(9);
  }
}

/**
 * Four layers: tables at layers 1 and 3, carry-forward at 2 and 4. Legacy
 * flags 3, 9 and 10, which share a body.
 */
function populateFourLayerTables(
  layer: number,
  state: RulesAddSubProblemState,
  layer1: ProblemPool,
  layer3: ProblemPool,
): void {
  if (layer == 2 || layer == 4) {
    if (isFirstTime(state, layer)) {
      carryForwardUnchosen(
        state.topEQs,
        state.bottomAnswers,
        state.blnChosen[layer - 2]!,
      );
      markVisited(state, layer);
    }
    state.blnChosen[layer - 1] = zeros(9);
    return;
  }

  if (isFirstTime(state, layer)) {
    loadPool(state, layer == 1 ? layer1 : layer3);
    markVisited(state, layer);
  }
  state.blnChosen[layer - 1] = zeros(18);
}

/** Layer 1 pool depends on the round; later layers carry forward. */
function populateFlag4(
  layer: number,
  round: number,
  state: RulesAddSubProblemState,
): void {
  if (layer == 1) {
    if (isFirstTime(state, layer)) {
      if (round == 1 || round == 3) {
        loadPool(state, RULES_ADD_SUB_DATA.flag4.layer1Round1Or3);
      } else if (round == 2) {
        loadPool(state, RULES_ADD_SUB_DATA.flag4.layer1Round2);
      } else {
        loadPool(state, RULES_ADD_SUB_DATA.flag4.layer1Round4);
      }
      markVisited(state, layer);
    }
    state.blnChosen[layer - 1] = zeros(round < 4 ? 45 : 91);
    return;
  }

  if (isFirstTime(state, layer)) {
    carryForwardUnchosen(
      state.topEQs,
      state.bottomAnswers,
      state.blnChosen[layer - 2]!,
    );
    markVisited(state, layer);
  }

  state.blnChosen[layer - 1] = zeros(flag4ChosenLength(layer, round));
}

function flag4ChosenLength(layer: number, round: number): number {
  if (round < 4) {
    if (layer == 2) {
      return 36;
    }
    return layer == 3 ? 27 : 18;
  }
  if (layer == 2) {
    return 81;
  }
  return layer == 3 ? 72 : 63;
}

const FLAG5_RULES_BY_ROUND: Record<number, GenerateRule> = {
  1: flag5Round1,
  2: flag5Round2,
  3: flag5Round3,
  4: flag5Round4,
};

/**
 * Flag 5 generates its problems rather than reading a table. The legacy loop
 * was `while (c <= intColumns * 3)` starting at 0, which yields
 * `intColumns * 3 + 1` problems - preserved deliberately.
 */
function populateFlag5(
  layer: number,
  round: number,
  columns: number,
  state: RulesAddSubProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  state.topEQs = [];
  state.bottomAnswers = [];
  state.blnChosen[layer - 1] = [];

  const rule = FLAG5_RULES_BY_ROUND[round];
  if (!rule) {
    // The original wires generation for rounds 1-4 only.
    return;
  }

  const problems = buildProblemSet(
    { kind: 'generate', rule, count: columns * 3 + 1 },
    randomInt,
  );

  state.topEQs = problems.topEQs;
  state.bottomAnswers = problems.bottomAnswers;
  state.blnChosen[layer - 1] = zeros(state.topEQs.length);
}

/**
 * Flags 11 and 12 generate their problems rather than reading a table. Flag 12
 * tightens its rule after round 2 (`VIFRulesAddSub.ts:552-557`).
 *
 * The `columns * 3 + 1` count preserves the legacy off-by-one: the original
 * loop was `while (c <= intColumns * 3)` starting at 0.
 */
function populateGenerated(
  flag: 11 | 12,
  layer: number,
  round: number,
  columns: number,
  state: RulesAddSubProblemState,
  randomInt: (min: number, max: number) => number,
): void {
  const rule: GenerateRule =
    flag === 11 ? flag11 : round <= 2 ? flag12Strict : flag12Mixed;

  const problems = buildProblemSet(
    { kind: 'generate', rule, count: columns * 3 + 1 },
    randomInt,
  );

  state.topEQs = problems.topEQs;
  state.bottomAnswers = problems.bottomAnswers;
  state.blnChosen[layer - 1] = zeros(state.topEQs.length);
}
